import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import { Lock, Unlock, RefreshCw, AlertCircle, Clock, History, Bike, X } from 'lucide-react';
import { Badge } from '../ui/badge';

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const tempoDecorrido = (iso) => {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const horas = Math.floor(ms / 3600000);
    if (horas < 1) return `há ${Math.max(1, Math.floor(ms / 60000))} min`;
    if (horas < 24) return `há ${horas}h`;
    const dias = Math.floor(horas / 24);
    return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  } catch {
    return '';
  }
};

const labelOrigem = (o) => {
  switch (o) {
    case 'automatico_atraso_3d': return '🤖 Automático (3+ dias atraso)';
    case 'manual_admin': return '👤 Manual (admin)';
    case 'automatico_pagamento': return '✅ Automático (pagamento aprovado)';
    default: return o || '-';
  }
};

const MotosBloqueadasPage = () => {
  const [bloqueios, setBloqueios] = useState([]);
  const [motos, setMotos] = useState({});
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('bloqueada'); // bloqueada | liberada | todos
  const [acaoEmAndamento, setAcaoEmAndamento] = useState(null); // moto_id sendo processada
  const [historicoModal, setHistoricoModal] = useState(null); // doc com histórico
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const [respBloqueios, respMotos, respClientes] = await Promise.all([
        apiService.api.get('/admin/brasilsat/bloqueios-motos'),
        apiService.api.get('/motos').catch(() => ({ data: [] })),
        apiService.api.get('/clientes').catch(() => ({ data: [] })),
      ]);
      setBloqueios(respBloqueios.data?.bloqueios || []);
      const motosMap = {};
      (respMotos.data || []).forEach((m) => { motosMap[m.id] = m; });
      setMotos(motosMap);
      const clientesMap = {};
      (respClientes.data || []).forEach((c) => { clientesMap[c.id] = c; });
      setClientes(clientesMap);
    } catch (e) {
      setErro(e?.response?.data?.detail || e.message || 'Erro ao carregar bloqueios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const desbloquearMoto = async (moto_id, placa) => {
    if (!window.confirm(`Confirmar DESBLOQUEIO do motor da moto ${placa || moto_id.slice(0, 8)}?\n\nIsso enviará o comando RELAY,0 para o rastreador Brasil Sat e a moto poderá dar partida novamente.`)) {
      return;
    }
    setAcaoEmAndamento(moto_id);
    try {
      await apiService.api.post('/admin/brasilsat/desbloquear-moto', {
        moto_id,
        motivo: 'Desbloqueio manual pelo painel admin',
      });
      await carregar();
    } catch (e) {
      alert('Falha ao desbloquear: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setAcaoEmAndamento(null);
    }
  };

  const bloquearMoto = async (moto_id, placa) => {
    const motivo = window.prompt(`Confirmar BLOQUEIO do motor da moto ${placa || moto_id.slice(0, 8)}?\n\nIsso enviará RELAY,1 ao rastreador. Motivo:`, 'Bloqueio manual');
    if (!motivo) return;
    setAcaoEmAndamento(moto_id);
    try {
      await apiService.api.post('/admin/brasilsat/bloquear-moto', { moto_id, motivo });
      await carregar();
    } catch (e) {
      alert('Falha ao bloquear: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setAcaoEmAndamento(null);
    }
  };

  const bloqueiosFiltrados = bloqueios.filter((b) => {
    if (filtro === 'todos') return true;
    return b.status === filtro;
  });

  const totalBloqueadas = bloqueios.filter((b) => b.status === 'bloqueada').length;
  const totalLiberadas = bloqueios.filter((b) => b.status === 'liberada').length;

  return (
    <div className="p-6 space-y-6" data-testid="motos-bloqueadas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Lock className="h-7 w-7 text-red-500" />
            Motos Bloqueadas
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Histórico e estado atual dos bloqueios de motor via Brasil Sat (RELAY)
          </p>
        </div>
        <button
          onClick={carregar}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
          data-testid="btn-recarregar-bloqueios"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-red-950/60 to-gray-900 border border-red-500/30 rounded-xl p-5">
          <div className="text-xs text-red-400 uppercase tracking-wider font-semibold">Bloqueadas agora</div>
          <div className="text-4xl font-bold text-red-400 mt-2" data-testid="metric-bloqueadas-agora">{totalBloqueadas}</div>
          <div className="text-xs text-gray-500 mt-1">motor cortado via rastreador</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-950/60 to-gray-900 border border-emerald-500/30 rounded-xl p-5">
          <div className="text-xs text-emerald-400 uppercase tracking-wider font-semibold">Liberadas</div>
          <div className="text-4xl font-bold text-emerald-400 mt-2" data-testid="metric-liberadas">{totalLiberadas}</div>
          <div className="text-xs text-gray-500 mt-1">desbloqueadas no histórico</div>
        </div>
        <div className="bg-gradient-to-br from-blue-950/60 to-gray-900 border border-blue-500/30 rounded-xl p-5">
          <div className="text-xs text-blue-400 uppercase tracking-wider font-semibold">Total no histórico</div>
          <div className="text-4xl font-bold text-blue-400 mt-2">{bloqueios.length}</div>
          <div className="text-xs text-gray-500 mt-1">eventos registrados</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'bloqueada', label: 'Bloqueadas agora', count: totalBloqueadas, color: 'red' },
          { key: 'liberada', label: 'Liberadas', count: totalLiberadas, color: 'emerald' },
          { key: 'todos', label: 'Todos', count: bloqueios.length, color: 'blue' },
        ].map(({ key, label, count, color }) => (
          <button
            key={key}
            onClick={() => setFiltro(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
              filtro === key
                ? `bg-${color}-500/20 text-${color}-300 border-${color}-500/50`
                : 'bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700'
            }`}
            data-testid={`filtro-${key}`}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      {erro && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="text-sm">{erro}</div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-10 text-center text-gray-400">
          Carregando bloqueios...
        </div>
      ) : bloqueiosFiltrados.length === 0 ? (
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-10 text-center" data-testid="empty-bloqueios">
          <Bike className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <div className="text-gray-300 font-medium">Nenhuma moto neste filtro</div>
          <div className="text-gray-500 text-sm mt-1">
            {filtro === 'bloqueada' && '🎉 Não há motos com motor bloqueado no momento.'}
            {filtro === 'liberada' && 'Nenhum desbloqueio registrado ainda.'}
            {filtro === 'todos' && 'Nenhum bloqueio jamais foi registrado.'}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {bloqueiosFiltrados.map((b) => {
            const moto = motos[b.moto_id];
            const cliente = clientes[b.cliente_id];
            const isBloqueada = b.status === 'bloqueada';
            const placa = moto?.placa || '-';

            return (
              <div
                key={b.moto_id}
                className={`rounded-xl border p-5 transition-all ${
                  isBloqueada
                    ? 'bg-gradient-to-br from-red-950/30 to-gray-900 border-red-500/40'
                    : 'bg-gradient-to-br from-emerald-950/20 to-gray-900 border-emerald-500/30'
                }`}
                data-testid={`bloqueio-card-${b.moto_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-xl font-bold text-white">{placa}</div>
                      <Badge className={isBloqueada ? 'bg-red-500/20 text-red-400 border-red-500/40' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'}>
                        {isBloqueada ? '🔒 Bloqueada' : '✅ Liberada'}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-400 mt-0.5">
                      {moto?.modelo || 'DK 160'} · {moto?.cor || '-'}
                    </div>
                  </div>
                  <button
                    onClick={() => setHistoricoModal(b)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    title="Ver histórico completo"
                    data-testid={`btn-historico-${b.moto_id}`}
                  >
                    <History className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  {cliente && (
                    <div className="flex gap-2">
                      <span className="text-gray-500">Cliente:</span>
                      <span className="text-white">{cliente.nome}</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <span className="text-gray-500">IMEI:</span>
                    <span className="text-gray-300 font-mono text-xs">{b.imei || '-'}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-500">Origem:</span>
                    <span className="text-gray-300">{labelOrigem(b.origem)}</span>
                  </div>
                  {b.motivo && (
                    <div className="flex gap-2">
                      <span className="text-gray-500">Motivo:</span>
                      <span className="text-gray-300">{b.motivo}</span>
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <Clock className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-gray-400 text-xs">
                      {isBloqueada
                        ? `Bloqueada em ${fmtDateTime(b.bloqueada_em)} (${tempoDecorrido(b.bloqueada_em)})`
                        : `Liberada em ${fmtDateTime(b.liberada_em)} (${tempoDecorrido(b.liberada_em)})`}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-800 flex gap-2">
                  {isBloqueada ? (
                    <button
                      onClick={() => desbloquearMoto(b.moto_id, placa)}
                      disabled={acaoEmAndamento === b.moto_id}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                      data-testid={`btn-desbloquear-${b.moto_id}`}
                    >
                      <Unlock className="h-4 w-4" />
                      {acaoEmAndamento === b.moto_id ? 'Enviando...' : 'Desbloquear motor'}
                    </button>
                  ) : (
                    <button
                      onClick={() => bloquearMoto(b.moto_id, placa)}
                      disabled={acaoEmAndamento === b.moto_id}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                      data-testid={`btn-bloquear-${b.moto_id}`}
                    >
                      <Lock className="h-4 w-4" />
                      {acaoEmAndamento === b.moto_id ? 'Enviando...' : 'Bloquear novamente'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal histórico */}
      {historicoModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setHistoricoModal(null)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="historico-modal"
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Histórico — {motos[historicoModal.moto_id]?.placa || historicoModal.moto_id.slice(0, 8)}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">IMEI {historicoModal.imei}</p>
              </div>
              <button
                onClick={() => setHistoricoModal(null)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3">
              {(historicoModal.historico || []).slice().reverse().map((h, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    h.acao === 'bloquear'
                      ? 'bg-red-500/5 border-red-500/30'
                      : 'bg-emerald-500/5 border-emerald-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-white text-sm flex items-center gap-1.5">
                      {h.acao === 'bloquear' ? <Lock className="h-3.5 w-3.5 text-red-400" /> : <Unlock className="h-3.5 w-3.5 text-emerald-400" />}
                      {h.acao === 'bloquear' ? 'Bloqueio' : 'Desbloqueio'}
                    </div>
                    <div className="text-xs text-gray-500">{fmtDateTime(h.em)}</div>
                  </div>
                  <div className="text-xs text-gray-400 mt-1.5 space-y-0.5">
                    <div><span className="text-gray-500">Por:</span> {h.por || 'sistema'}</div>
                    <div><span className="text-gray-500">Origem:</span> {labelOrigem(h.origem)}</div>
                    {h.motivo && <div><span className="text-gray-500">Motivo:</span> {h.motivo}</div>}
                    {h.resposta && (
                      <div className="text-xs text-gray-500 mt-1">
                        Resposta Brasil Sat: {h.resposta.sucesso ? '✅' : '❌'} {h.resposta.message || `code ${h.resposta.code}`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(!historicoModal.historico || historicoModal.historico.length === 0) && (
                <div className="text-center text-gray-500 text-sm py-6">Sem histórico registrado.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MotosBloqueadasPage;
